import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SEOHeadTags, useSEO } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/videoData';
import { categoryVideos, categoryPageMeta } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'dianshiju';
  const [prevSlug, setPrevSlug] = useState(currentSlug);

  useEffect(() => {
    if (prevSlug !== currentSlug) {
      setPrevSlug(currentSlug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSlug, prevSlug]);

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const videos = categoryVideos[currentSlug] || [];
  const meta = categoryPageMeta[currentSlug] || categoryPageMeta.dianshiju;

  const seo = useSEO({
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    canonicalUrl: `https://guochan.video/category/${currentSlug}`,
  });

  return (
    <>
      <SEOHeadTags data={seo} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-24 pb-6 bg-background-100">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
                {currentCategory?.name} · 国产视频在线观看
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                {currentCategory?.description}
              </p>
            </div>
          </div>

          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-5 overflow-x-auto flex-wrap border-b border-background-200/70">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="py-8">
              {videos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                  {videos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/category/${currentSlug}`}
                      className="group cursor-pointer block"
                    >
                      <div className="relative overflow-hidden rounded-xl bg-background-200 aspect-[3/4]">
                        <img
                          src={video.image}
                          alt={video.title}
                          title={`${video.title} - 国产视频在线观看`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-md">
                            {video.rating}
                          </span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <p className="text-xs text-white/70 mb-1">{video.year}</p>
                          <h3 className="text-sm md:text-base font-bold text-white leading-tight line-clamp-2">
                            {video.title}
                          </h3>
                          <p className="text-xs text-white/60 mt-1.5">{video.updateInfo}</p>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50">
                            <i className="ri-play-fill text-2xl ml-0.5"></i>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-sm text-foreground-500 line-clamp-2">{video.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-video-line text-3xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的视频资源</p>
                  <Link
                    to="/"
                    className="inline-block mt-4 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    返回首页
                  </Link>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}